import axios from "axios";
import {
  clearAuthStorage,
  getAccessToken,
  getRefreshToken,
  getUserId,
  isTokenExpired,
  persistAuthTokens,
} from "./utils/authSession";

const REACT_APP_BASE_URL = process.env.REACT_APP_BASE_URL;

const refreshToken = async (currentUser) => {
  const rToken = getRefreshToken();
  const userID = getUserId(currentUser);
  if (!rToken || !userID) return null;

  try {
    const res = await axios.post(
      `${REACT_APP_BASE_URL}user/refresh-token`,
      {},
      {
        headers: {
          user: userID,
          token: rToken,
        },
      }
    );
    persistAuthTokens(res.data?.metadata?.tokens);
    return res.data;
  } catch (error) {
    clearAuthStorage();
    return null;
  }
};

const attachAuthHeaders = (config, currentUser, accessToken) => {
  const userID = getUserId(currentUser);

  if (!config.headers) config.headers = {};
  if (accessToken) {
    config.headers["authorization"] = accessToken;
  } else {
    delete config.headers["authorization"];
  }
  if (userID) config.headers["user"] = userID;

  return config;
};

export const createAxios = (currentUser) => {
  const newInstance = axios.create();
  newInstance.interceptors.request.use(
    async (config) => {
      const accessToken = getAccessToken(currentUser);

      if (isTokenExpired(accessToken)) {
        const data = await refreshToken(currentUser);
        if (!data?.metadata?.tokens?.accessToken) {
          return Promise.reject(new Error("Authentication expired"));
        }

        attachAuthHeaders(config, currentUser, data.metadata.tokens.accessToken);
        config.headers["token"] = data.metadata.tokens.refreshToken;
        return config;
      }
      return attachAuthHeaders(config, currentUser, accessToken);
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  newInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;
        const data = await refreshToken(currentUser);

        if (data?.metadata?.tokens?.accessToken) {
          attachAuthHeaders(
            originalRequest,
            currentUser,
            data.metadata.tokens.accessToken
          );
          return newInstance(originalRequest);
        }
      }

      return Promise.reject(error);
    }
  );

  return newInstance;
};
